/**
 * User/Profile API functions
 */

import { apiClient, type PaginatedResponse } from './client'
import { buildPaginationParams, type PaginationOptions } from './pagination'
import LogTool from '../logger/logTools'
import { createUser } from '../utils/functions'
import { type User } from '../utils/types'

const log = new LogTool({ context: 'UserAPI', enable: true, logLevel: 'warn' })

/**
 * Convert form input to API JSON
 */
export function formInputToAPIUserJSON(input: any): any {
  return {
    ...input,
    ...(input.firstName && { first_name: input.firstName }),
    ...(input.lastName && { last_name: input.lastName }),
    ...(input.email && { email: input.email }),
  }
}

/**
 * Fetch paginated users from API
 */
export async function fetchUsers(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<User>> {
  log.info('Fetching users with options:', options)

  const params = buildPaginationParams(options)
  const response = await apiClient.get<PaginatedResponse<any>>(`v1/accounts/users/?${params.join('&')}`)

  const users = response.results.map((u: any) => createUser(u))

  return {
    ...response,
    results: users,
  }
}

/**
 * Fetch current logged-in user
 */
export async function fetchCurrentUser(expand?: string[]): Promise<User> {
  log.info('Fetching current user')

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const url = params.length > 0 ? `v1/accounts/users/self/?${params.join('&')}` : 'v1/accounts/users/self/'

  const response = await apiClient.get<any>(url)
  return createUser(response)
}

/**
 * Fetch a single user by ID or URL
 */
export async function fetchUser(
  idOrUrl: string | number,
  expand?: string[]
): Promise<User> {
  log.info('Fetching user:', idOrUrl)

  const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? `v1/accounts/users/${idOrUrl}/`
    : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createUser(response)
}

/**
 * Create a new user
 */
export async function createUserApi(userData: any): Promise<User> {
  log.info('Creating user:', userData)

  const json = formInputToAPIUserJSON(userData)
  const response = await apiClient.post<any>('v1/accounts/users/', json)

  return createUser(response)
}

/**
 * Update current user profile
 */
export async function updateCurrentUserApi(userData: any): Promise<User> {
  log.info('Updating current user profile')

  const json = formInputToAPIUserJSON(userData)
  const response = await apiClient.patch<any>('v1/accounts/users/self/', json)

  return createUser(response)
}

/**
 * Update an existing user
 */
export async function updateUserApi(
  idOrSelf: string | number,
  userData: any
): Promise<User> {
  log.info('Updating user:', idOrSelf, userData)

  const url = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? `v1/accounts/users/${idOrSelf}/`
    : idOrSelf

  const json = formInputToAPIUserJSON(userData)
  const response = await apiClient.put<any>(url, json)

  return createUser(response)
}

/**
 * Patch an existing user (partial update)
 */
export async function patchUserApi(
  idOrSelf: string | number,
  userData: Partial<any>
): Promise<User> {
  log.info('Patching user:', idOrSelf, userData)

  const url = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? `v1/accounts/users/${idOrSelf}/`
    : idOrSelf

  const json = formInputToAPIUserJSON(userData)
  const response = await apiClient.patch<any>(url, json)

  return createUser(response)
}

/**
 * Delete a user
 */
export async function deleteUserApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting user:', idOrSelf)

  const url = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? `v1/accounts/users/${idOrSelf}/`
    : idOrSelf

  await apiClient.delete(url)
}

/**
 * Delete current user (self)
 */
export async function deleteCurrentUserApi(): Promise<void> {
  log.info('Deleting current user (self)')

  await apiClient.delete('v1/accounts/users/self/')
}

/**
 * Update current user preferences
 */
export async function updateCurrentUserPreferencesApi(preferences: { language?: string }): Promise<User> {
  log.info('Updating current user preferences:', preferences)

  const response = await apiClient.patch<any>('v1/accounts/users/self/preferences/', preferences)

  return createUser(response)
}

/**
 * Fetch all staff users (recursively fetches all pages, filtered for staff)
 * Excludes system accounts like chat@assemblean.com
 * Note: Use with caution for large datasets
 */
export async function fetchAllStaffUsers(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<User[]> {
  log.info('Fetching all staff users')

  const excludedEmails = ['chat@test.assemblean.com', 'admin@assemblean.com', 'chat@assemblean.com']
  let allStaffUsers: User[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchUsers({ ...options, page })
    
    // Filter for staff users and exclude system accounts
    const staffUsers = response.results.filter(
      (user: User) => user.isStaff && user.email && !excludedEmails.includes(user.email)
    )
    
    allStaffUsers = [...allStaffUsers, ...staffUsers]
    
    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allStaffUsers.length} staff users total`)
  return allStaffUsers
}

/**
 * Fetch all staff users as options for select inputs
 * Returns options in the format: { value: string, label: string }
 * Label format: "firstName lastName" or email if no name available
 * Excludes system accounts like chat@assemblean.com
 * Note: Use with caution for large datasets
 */
export async function fetchStaffUserOptions(): Promise<
  { value: string; label?: string; disabled?: boolean }[]
> {
  log.info('Fetching staff user options')

  const allStaffUsers = await fetchAllStaffUsers()
  
  return allStaffUsers
    .filter(user => user.self !== undefined)
    .map(user => {
      const hasName = user.firstName || user.lastName
      const label = hasName
        ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
        : user.email ?? ''
      
      return {
        value: user.self,
        label,
      }
    })
}

